import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  SettingFilled,
  FileExcelOutlined,
  FilePdfOutlined,
  FileTextOutlined
} from '@ant-design/icons';
import './styles.css';
import { Select } from '../../components/Select';
import {
  FONTSIZE,
  GAEventIdentifier,
  GAEventName,
  LOCKED_COLUMNS,
  ROLE_DEFAULT_VISIBLE_COLUMNS,
  SOURCE_FORMAT
} from '../../section/Common/constants';
import { TColumns } from '../../section/Common/types/types';
import CustomPopover from '../../components/Popover';
import ExportData from '../ExportData/ExportData';
import generateXLS from '../../section/Common/utils/generateXLS';
import generatePDF from '../../section/Common/utils/generatePDF';
import { useGenreController } from '../../context/GenreController.context';
import { ACTIONS_GENRE } from '../../core/constants/global-constants';
import { useUserPermit } from '../../context/User.context';
import { useActions } from '../../context/Actions.context';
import { MUTED_AUTHORS, SOURCE, TOP_POD_TITLES } from '../../core/constants/endpoints';
import ArrangeColumnsModal, {
  buildVisibleColumnPreferences,
  loadColumnPreferences,
  mergeWithLockedColumns,
  normalizeFixedColumns,
  reconcilePreferences
} from '../ArrangeColumnsModal';
import { useGetColumnPreferences } from '../../core/modules/book/hooks/useBrendaColumnPreferences';

interface ISettingsProps {
  id: string;
  setColumns: Function;
  thead: TColumns[];
  changeFontSize: Function;
  transientColumns?: string[];
}

const Settings: React.FC<ISettingsProps> = ({
  thead,
  id,
  changeFontSize,
  setColumns,
  transientColumns = []
}) => {
  const { source: sourcePath } = useActions();
  const [isArrangeColumnsVisible, setIsArrangeColumnsVisible] = useState<boolean>(false);

  // Hidden column names - kept in state (rather than derived) so it can also
  // be handed to ExportData as the set of columns to exclude from exports.
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);

  // Visible column keys in on-screen order, so exports mirror the arranged
  // table. Empty until a layout is resolved, which keeps the exporters on
  // the tab's static column order.
  const [exportColumns, setExportColumns] = useState<string[]>([]);
  const showGenreOptions = (sourcePath['source'] === SOURCE.bookstat) && id !== TOP_POD_TITLES && id !== MUTED_AUTHORS;
  const { edit_titles, role } = useUserPermit();

  const allColumnNames = useMemo(
    () => thead.map(column => column.key),
    [thead]
  );

  // Keyed on a serialized signature rather than the raw arrays: `transientColumns`
  // defaults to a fresh `[]` each render, which would otherwise recompute this
  // memo (and everything derived from it) on every render.
  const preferenceColumnNames = useMemo(
    () =>
      allColumnNames.filter(
        column => !transientColumns.includes(column)
      ),
    [allColumnNames.join('|'), transientColumns.join('|')]
  );

  // Role-based default visibility for users who have never saved column
  // preferences for this table. Resolves the current source (bookstat, ao3,
  // royalroad) and looks up the configured allow-list for the user's role.
  // `roleVisibleColumns` is either a stable module-level constant or undefined,
  // so this memo stays referentially stable across renders - important because
  // it feeds the column-apply effect's dependency array.
  const source = SOURCE_FORMAT[sourcePath['source']];
  const roleVisibleColumns = ROLE_DEFAULT_VISIBLE_COLUMNS[role]?.[source];
  const roleDefaultPreferences = useMemo(
    () =>
      buildVisibleColumnPreferences(
        roleVisibleColumns,
        preferenceColumnNames,
        LOCKED_COLUMNS
      ),
    [roleVisibleColumns, preferenceColumnNames]
  );

  // Applies a given ordered/hidden set of column names to the actual thead
  // objects, preserving each column's own data and re-ordering/filtering it.
  const applyColumnSelection = useCallback(
    (orderedNames: string[], hiddenNames: string[]) => {
      const visibleColumns = orderedNames
        .filter(name => !hiddenNames.includes(name))
        .map(name => thead.find(column => column.key === name))
        .filter((column): column is TColumns => Boolean(column));

      const excludedColumns = thead.filter(column =>
        transientColumns.includes(column.key)
      );

      // Re-derive antd's fixed columns by position so the pinned slots stay
      // contiguous at the table edges. A scrollable column dragged into a pinned
      // slot becomes pinned there (instead of scrolling away and leaving a gap).
      setColumns(normalizeFixedColumns([
        ...visibleColumns,
        ...excludedColumns
      ]));

      setHiddenColumns(hiddenNames);
      setExportColumns(visibleColumns.map(column => column.key));
    },
    [thead, setColumns, transientColumns]
  );

  const { data: savedPrefs, isLoading: isLoadingPrefs } = useGetColumnPreferences(id);

  // Apply any previously saved column preferences whenever the underlying
  // thead or savedPrefs changes, so saved order/visibility persists.
  useEffect(() => {
    const saved = savedPrefs || loadColumnPreferences(id);

    if (saved) {
      // Reconcile against the current (non-transient) schema before merging so
      // columns added since the user's last save are appended rather than
      // silently dropped. Shared with the modal's re-hydration path.
      const { order, hidden } = reconcilePreferences(preferenceColumnNames, LOCKED_COLUMNS, saved);
      const fullOrder = mergeWithLockedColumns(allColumnNames, LOCKED_COLUMNS, order);
      applyColumnSelection(fullOrder, hidden);

      return;
    }

    if (isLoadingPrefs) {
      return;
    }

    // No saved preferences yet: fall back to the role-based default, which shows
    // only a curated set of columns and hides the rest (still unhideable via the
    // Arrange Columns modal). Locked columns are never part of `hidden`.
    if (roleDefaultPreferences) {
      const fullOrder = mergeWithLockedColumns(
        allColumnNames,
        LOCKED_COLUMNS,
        roleDefaultPreferences.order
      );
      applyColumnSelection(fullOrder, roleDefaultPreferences.hidden);

      return;
    }

    if (sourcePath['source'].includes(SOURCE['daily-rank'])) {
      applyColumnSelection(allColumnNames, []);
    }
  }, [JSON.stringify(thead), savedPrefs, isLoadingPrefs, roleDefaultPreferences]);

  const handleArrangeColumnsSave = ({
    fullOrder,
    hidden
  }: {
    fullOrder: string[];
    hidden: string[];
  }) => {
    setIsArrangeColumnsVisible(false);
    applyColumnSelection(fullOrder, hidden);
  };

  const handleProvinceChange = value => {
    changeFontSize(value);
  };

  const { handleGenreAction } = useGenreController();

  const settingsContent = (
    <ul className='settings-menu'>
      <li
        className='settings-menu-item'
      >
        <button
          className='basic-btn'
          onClick={() => {
            window.gtag('event', GAEventName.click, {
              event_category: 'Interaction',
              identifier: GAEventIdentifier.openArrangeColumns
            });
            setIsArrangeColumnsVisible(true);
          }}
        >
            Arrange columns
        </button>
      </li>
      <li className='settings-menu-item'>
        Font size:
        <Select
          className='settings-menu-item-font-size'
          defaultValue={FONTSIZE[3].value}
          style={{ width: 120 }}
          onChange={handleProvinceChange}
          options={FONTSIZE}
        />
      </li>
      <li>
        <ExportData
          generateFile={generateXLS}
          typeFile={'xlsx'}
          text={'Export Excel'}
          endpoint={id}
          checkedTitles={hiddenColumns}
          exportColumns={exportColumns}
        >
          <FileExcelOutlined className='file-excel' />
        </ExportData>
      </li>
      <li>
        <ExportData
          generateFile={generatePDF}
          typeFile={'pdf'}
          text={'Export PDF'}
          endpoint={id}
          checkedTitles={hiddenColumns}
          exportColumns={exportColumns}
        >
          <FilePdfOutlined className='file-pdf' />
        </ExportData>
      </li>
      <li>
        <ExportData
          generateFile={generateXLS}
          typeFile={'csv'}
          text={'Export CSV'}
          endpoint={id}
          checkedTitles={hiddenColumns}
          exportColumns={exportColumns}
        >
          <FileTextOutlined className='file-csv' />
        </ExportData>
      </li>

      {edit_titles && showGenreOptions &&
        <>
          <li >
            <button
              className='btn-genre'
              onClick={() => {
                window.gtag('event', GAEventName.click, {
                  event_category: 'Interaction',
                  identifier: ACTIONS_GENRE.create,
                  content_type: 'genre'
                });
                handleGenreAction(ACTIONS_GENRE.create);
              }}
            > Create custom genre
            </button>

          </li>
          <li>
            <button
              className='btn-genre'
              onClick={() => {
                window.gtag('event', GAEventName.click, {
                  event_category: 'Interaction',
                  identifier: ACTIONS_GENRE.edit,
                  content_type: 'genre'
                });
                handleGenreAction(ACTIONS_GENRE.edit);
              }}
            > Edit custom genre
            </button>
          </li>
          <li>
            <button
              className='btn-genre'
              onClick={() => {
                window.gtag('event', GAEventName.click, {
                  event_category: 'Interaction',
                  identifier: ACTIONS_GENRE.delete,
                  content_type: 'genre'
                });
                handleGenreAction(ACTIONS_GENRE.delete);
              }}
            > Delete custom genre
            </button>
          </li>
        </>}
    </ul>
  );

  return (
    <>
      <ArrangeColumnsModal
        visible={isArrangeColumnsVisible}
        tableId={id}
        allColumns={preferenceColumnNames}
        lockedColumns={LOCKED_COLUMNS}
        roleDefaultPreferences={roleDefaultPreferences}
        onCancel={() => setIsArrangeColumnsVisible(false)}
        onSave={handleArrangeColumnsSave}
      />

      <button
        className='settings basic-btn'
        onMouseEnter={() => {
          window.gtag('event', GAEventName.hover,
            { event_category: 'Interaction', identifier: GAEventIdentifier.openSettings });
        }}
      >
        <CustomPopover
          content={settingsContent}
          title='Settings'
          trigger='hover'
          placement='bottomRight'
        >
          <SettingFilled data-testid='settings-btn' />
        </CustomPopover>
      </button>
    </>
  );
};

export default Settings;